import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, Check, X, Loader2 } from 'lucide-react';

const ComptabiliteRecettes = ({ 
  coproprietaires, 
  recettes, 
  selectedYear, 
  coproId, 
  apiClient, 
  getErrorMessage, 
  onRefresh 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importedData, setImportedData] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRecette, setEditingRecette] = useState(null);
  
  const [formData, setFormData] = useState({
    coproprietaire_id: '', 
    montant: '', 
    date: new Date().toISOString().split('T')[0], 
    description: ''
  });

  const getCoproRecettes = (coproId) => recettes.filter(r => r.coproprietaire_id === coproId);
  const getCoproTotal = (coproId) => getCoproRecettes(coproId).reduce((s, r) => s + parseFloat(r.montant || 0), 0);
  const totalRecettes = recettes.reduce((s, r) => s + parseFloat(r.montant || 0), 0);

  const resetForm = () => {
    setFormData({ coproprietaire_id: '', montant: '', date: new Date().toISOString().split('T')[0], description: '' });
    setEditingRecette(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!formData.coproprietaire_id || !formData.montant) {
      toast.error('Veuillez sélectionner un copropriétaire et saisir un montant');
      return;
    }
    setSubmitting(true);
    try {
      if (editingRecette) {
        await apiClient.put('/coproprietes/' + coproId + '/comptabilite/recettes/' + editingRecette.id, { 
          ...formData, 
          annee: selectedYear 
        });
        toast.success('Recette modifiée');
      } else {
        await apiClient.post('/coproprietes/' + coproId + '/comptabilite/recettes', { 
          ...formData, 
          annee: selectedYear 
        });
        toast.success('Recette ajoutée');
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (recette) => {
    setFormData({
      coproprietaire_id: recette.coproprietaire_id,
      montant: recette.montant,
      date: recette.date?.split('T')[0] || recette.date,
      description: recette.description || ''
    });
    setEditingRecette(recette);
    setShowForm(true);
  };

  const handleDelete = async (recetteId) => {
    if (!window.confirm('Supprimer cette recette ?')) return;
    try {
      await apiClient.delete('/coproprietes/' + coproId + '/comptabilite/recettes/' + recetteId);
      toast.success('Recette supprimée');
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }
    setProcessing(true);
    setShowImport(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('annee', selectedYear);
      const response = await apiClient.post('/coproprietes/' + coproId + '/comptabilite/import-releve', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportedData(response.data.recettes_detectees || []);
      if (response.data.recettes_detectees?.length > 0) {
        toast.success(response.data.recettes_detectees.length + ' paiements détectés par l\'IA');
      } else {
        toast.info('Aucun paiement détecté dans ce relevé');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'analyse'));
      setShowImport(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleValidateImport = async () => {
    const toImport = importedData.filter(r => r.coproprietaire_id);
    if (toImport.length === 0) {
      toast.error('Associez au moins un copropriétaire aux paiements à importer');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/coproprietes/' + coproId + '/comptabilite/recettes/bulk', { 
        recettes: toImport, 
        annee: selectedYear 
      });
      toast.success(toImport.length + ' recettes importées avec succès');
      setShowImport(false);
      setImportedData([]);
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  };

  const updateImported = (idx, field, value) => {
    const updated = [...importedData];
    updated[idx][field] = value;
    setImportedData(updated);
  };

  const removeImported = (idx) => {
    setImportedData(importedData.filter((_, i) => i !== idx));
  };

  const getCoproName = (id) => {
    const c = coproprietaires.find(cp => cp.id === id);
    return c ? (c.prenom + ' ' + c.nom).trim() : 'Inconnu';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Recettes par copropriétaire</h2>
          <p className="text-sm text-slate-500">Total : {totalRecettes.toFixed(2)} € sur {selectedYear}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => { resetForm(); setShowForm(true); }} variant="outline">
            + Saisie manuelle
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept="application/pdf" onChange={handleImportFile} className="hidden" />
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg cursor-pointer">
              Import relevé PDF
            </span>
          </label>
        </div>
      </div>

      {/* Formulaire ajout/modification */}
      {showForm && (
        <div className="bg-slate-50 rounded-xl p-6 border">
          <h3 className="font-semibold mb-4">{editingRecette ? 'Modifier la recette' : 'Nouvelle recette'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select 
              value={formData.coproprietaire_id} 
              onChange={(e) => setFormData({ ...formData, coproprietaire_id: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Copropriétaire *</option>
              {coproprietaires.map(c => (
                <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
              ))}
            </select>
            <input 
              type="number" 
              step="0.01" 
              placeholder="Montant € *" 
              value={formData.montant}
              onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
            <input 
              type="date" 
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
            <input 
              type="text" 
              placeholder="Description (optionnel)" 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingRecette ? 'Modifier' : 'Enregistrer'}
            </Button>
            <Button variant="outline" onClick={resetForm}>
              <X className="w-4 h-4 mr-2" />Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Import de relevé bancaire */}
      {showImport && (
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="font-semibold mb-4 text-blue-900">
            {processing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyse IA en cours...
              </span>
            ) : (
              importedData.length + ' paiement(s) détecté(s)'
            )}
          </h3>
          {!processing && importedData.length > 0 && (
            <div>
              <p className="text-sm text-blue-700 mb-4">
                Associez chaque paiement à un copropriétaire pour l'importer. Les paiements sans association seront ignorés.
              </p>
              <div className="bg-white rounded-lg border border-blue-200 overflow-x-auto mb-4">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Nom détecté</th>
                      <th className="px-3 py-2 text-left">Copropriétaire</th>
                      <th className="px-3 py-2 text-left">Montant</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedData.map((r, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">
                          <input 
                            type="date" 
                            value={r.date || ''} 
                            onChange={(e) => updateImported(idx, 'date', e.target.value)} 
                            className="px-2 py-1 border rounded text-xs w-32" 
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs max-w-32 truncate" title={r.nom_detecte}>
                          {r.nom_detecte}
                        </td>
                        <td className="px-3 py-2">
                          <select 
                            value={r.coproprietaire_id || ''} 
                            onChange={(e) => updateImported(idx, 'coproprietaire_id', e.target.value)} 
                            className={'px-2 py-1 border rounded text-xs w-full ' + (!r.coproprietaire_id ? 'border-amber-400 bg-amber-50' : 'border-emerald-400 bg-emerald-50')}
                          >
                            <option value="">-- Ignorer --</option>
                            {coproprietaires.map(c => (
                              <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input 
                            type="number" 
                            step="0.01" 
                            value={r.montant || ''} 
                            onChange={(e) => updateImported(idx, 'montant', e.target.value)} 
                            className="px-2 py-1 border rounded text-xs w-24" 
                          />
                          <span className="text-xs text-slate-500 ml-1">€</span>
                        </td>
                        <td className="px-3 py-2">
                          <input 
                            type="text" 
                            value={r.description || ''} 
                            onChange={(e) => updateImported(idx, 'description', e.target.value)} 
                            placeholder="Description..."
                            className="px-2 py-1 border rounded text-xs w-full" 
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeImported(idx)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-600">
                  {importedData.filter(r => r.coproprietaire_id).length} paiement(s) prêt(s) à importer
                </p>
                <div className="flex gap-3">
                  <Button onClick={handleValidateImport} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Importer les sélectionnés
                  </Button>
                  <Button variant="outline" onClick={() => { setShowImport(false); setImportedData([]); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          )}
          {!processing && importedData.length === 0 && (
            <div className="text-center py-4">
              <p className="text-blue-700">Aucun paiement entrant détecté dans ce relevé.</p>
              <Button variant="outline" onClick={() => setShowImport(false)} className="mt-3">
                Fermer
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Liste des recettes par copropriétaire */}
      {coproprietaires.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl border">
          <p className="text-slate-500">Aucun copropriétaire dans cette copropriété</p>
        </div>
      ) : (
        coproprietaires.map(copro => (
          <div key={copro.id} className="bg-white rounded-xl border overflow-hidden">
            <div className="flex items-center justify-between p-4 bg-slate-50 border-b">
              <div>
                <h3 className="font-semibold">{copro.prenom} {copro.nom}</h3>
                <p className="text-sm text-slate-500">{getCoproRecettes(copro.id).length} paiement(s)</p>
              </div>
              <p className="text-lg font-bold text-emerald-600">+{getCoproTotal(copro.id).toFixed(2)} €</p>
            </div>
            {getCoproRecettes(copro.id).length > 0 ? (
              <div className="divide-y">
                {getCoproRecettes(copro.id).map(r => (
                  <div key={r.id} className="flex items-center justify-between p-4 hover:bg-slate-50 group">
                    <div className="flex-1">
                      <p className="font-medium">{r.description || 'Paiement'}</p>
                      <p className="text-sm text-slate-500">
                        {r.date ? new Date(r.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-emerald-600">+{parseFloat(r.montant).toFixed(2)} €</p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => handleEdit(r)} 
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(r.id)} 
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-4 text-slate-500 text-center">Aucun paiement enregistré</p>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default ComptabiliteRecettes;
